<template>
  <section class="i-testimonials-section">
    <div class="container-wide">
      <div
        class="container"
        style="padding: 0"
      >
        <div class="label mb-3">Client results</div>
        <h2 class="headline">What happened<br />after the session.</h2>
      </div>

      <div
        class="grid-3"
        style="margin-top: 36px"
      >
        <div
          v-for="t in TESTIMONIALS"
          :key="t.name"
          class="testimonial-card"
        >
          <div class="testimonial-card__stars">★★★★★</div>
          <div class="testimonial-card__quote">"{{ t.quote }}"</div>
          <div class="testimonial-card__meta">
            <img
              class="testimonial-card__avatar"
              :src="t.image"
            />
            <div>
              <div class="testimonial-card__name">{{ t.name }}</div>
              <div class="testimonial-card__detail">{{ t.detail }}</div>
            </div>
          </div>
          <div class="testimonial-card__result">{{ t.result }}</div>
        </div>
      </div>

      <!-- Reviews note -->
      <div class="reviews-note">
        <div
          class="label mb-3"
          style="color: var(--muted)"
        >
          A note on our reviews
        </div>
        <p class="body-sm">
          Our average session rating is 4.8/5 from 6,847 reviews. Not every session produces a salary increase —
          outcomes depend on what you do with the plan. Our 4.8 reflects clients who rated the
          <em>quality of the advice and specificity of the plan</em>. The 4% who rated 3 stars or lower predominantly
          noted they didn't follow through on the plan. We include that transparently because we think you should know.
        </p>
      </div>

      <!-- Inline CTA -->
      <div
        class="container"
        style="padding: 0"
      >
        <div
          class="inline-cta"
          style="margin-top: 24px"
        >
          <div class="inline-cta-text">
            <strong>Ready?</strong> {{ timing }} minutes. A written plan. ${{ pricing }}.
            <div class="inline-proof">Career changers, job seekers, and people who aren't sure yet — all welcome</div>
          </div>
          <a
            class="btn-inline"
            href="#book"
            @click.prevent="$emit('book')"
            >Book — ${{ pricing }}</a
          >
        </div>
        <div
          v-if="!paymentProcessing"
          class="skip"
          @click="$emit('skip')"
        >
          No thanks - I'm fine figuring it out the hard way
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
defineEmits(["book", "skip"])

// ASSETS
import Alex from "@/assets/images/Alex.png"
import Ryan from "@/assets/images/Ryan.png"
import Dana from "@/assets/images/Dana.png"

// PROPS
defineProps({
  pricing: {
    type: String,
    default: "49"
  },
  timing: {
    type: String,
    default: "30"
  },
  paymentProcessing: {
    type: Boolean,
    default: false
  }
})

// CONSTANTS
const TESTIMONIALS = [
  {
    image: Dana,
    name: "Dana L.",
    detail: "Marketing Manager · Chicago, IL",
    quote:
      "She told me in 20 minutes what I'd been trying to figure out for 3 years. I had been targeting the wrong titles entirely. Changed my search, had 4 recruiter calls within 2 weeks.",
    result: "↑ Career change: Ops → People Analytics · $48K → $67K · 6 months after session"
  },
  {
    image: Ryan,
    name: "Ryan M.",
    detail: "Software Engineer · Austin, TX",
    quote:
      "I was skeptical. I've done career coaching before and always left with a framework. This was the first time I left with something I could actually do that afternoon.",
    result: "↑ Career change: Customer Support → Data Analyst · $46K → $92K · 1.2 years after session"
  },
  {
    image: Alex,
    name: "Alex T.",
    detail: "UX Designer · San Francisco, CA",
    quote:
      "The LinkedIn changes alone changed everything. I hadn't touched my profile in 2 years. He told me exactly what to change and why. Two job offers in the next 8 weeks.",
    result: "↑ Same field: Sales Development Rep → Account Executive · $50K → $105K · 18 months after session"
  }
]
</script>

<style lang="scss" scoped>
.i-testimonials-section {
  background: var(--purple-ultra);

  .testimonial-card {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 28px;
    box-shadow: var(--card-shadow);

    &__stars {
      color: var(--star);
      font-size: 1rem;
      letter-spacing: 1px;
      margin-bottom: 14px;
    }

    &__quote {
      font-size: 0.95rem;
      font-weight: 400;
      line-height: 1.65;
      color: var(--text-body);
      margin-bottom: 20px;
      font-style: italic;
    }

    &__meta {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    &__avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.875rem;
      font-weight: 700;
      color: #fff;
      flex-shrink: 0;
    }

    &__name {
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--text);
    }

    &__detail {
      font-size: 0.78rem;
      color: var(--muted);
    }

    &__result {
      background: var(--green-lt);
      border: 1px solid #10b98140;
      border-radius: var(--radius-sm);
      padding: 10px 14px;
      margin-top: 16px;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--green-dark);
    }
  }

  .reviews-note {
    margin-top: 20px;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 20px 24px;
    max-width: 580px;
    box-shadow: var(--card-shadow);
  }
}
</style>
